import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  AccessRequestActions,
  useAccessRequests,
  useAddFromRequest,
} from '@/features/workspaces';
import { ACCESS_COPY } from '@/features/workspaces/accessCopy';
import type { AccessRequestView } from '@/lib/api/generated/model';
import { cn } from '@/lib/utils';
import { UserAvatar } from '@/shared/components/UserAvatar';
import { formatRelativeTime } from '@/shared/utils/time';

/** The banner's heading: who waits, as a count (spec 194 US3). */
function askedToJoin(count: number): string {
  if (count === 0) return 'All answered';
  return count === 1
    ? '1 person asked to join'
    : `${String(count)} people asked to join`;
}

/**
 * How long an answered request takes to fold away (spec 196 US6): the scale's
 * `base`, which is what the row's transition runs at. Kept in step by hand with
 * `--motion-duration-base` (150ms) and a little over it, so the row is never
 * dropped mid-fold.
 */
const LEAVE_MS = 180;

/**
 * How long an answered request may wait for the list read again to drop it. The
 * service has accepted the answer; a refetch that fails leaves the list as it
 * was, and the row comes back as the list has it rather than stay folded.
 */
const LEAVE_FALLBACK_MS = 5000;

/**
 * Who asked to join, for an owner (spec 190): above the members, oldest first, and
 * absent when nobody waits. The same Add and Dismiss as the inbox's notice, so an
 * answer given in either place is given in both.
 *
 * At the top of People as a banner (spec 194 US3), headed by how many wait, so the
 * tab says first what it wants of its owner. Spec 196 US5 adds "Add as owner" to
 * each request and "Add all" to the banner; US6 has an answered request fold
 * away rather than vanish, while the person it added slides into the list below.
 */
export function AccessRequestsSection({
  workspaceId,
  canManage,
}: {
  workspaceId: string;
  canManage: boolean;
}) {
  const requests = useAccessRequests(workspaceId, canManage);
  const addFromRequest = useAddFromRequest();
  /**
   * Answered requests still on screen, folding: the service has accepted the
   * answer, and the list read again may already lack them. Each keeps its place.
   */
  const [leaving, setLeaving] = useState<
    ReadonlyMap<
      string,
      { request: AccessRequestView; at: number; since: number }
    >
  >(new Map());
  const [addingAll, setAddingAll] = useState(false);

  /*
    A folding row is let go once it has folded and the list read again no
    longer holds it. Letting go on the fold alone brought it back, unfolded and
    answerable, whenever the refetch took longer than the fold.
  */
  useEffect(() => {
    if (leaving.size === 0) return;
    const listed = new Set(requests.items.map((request) => request.id));
    // When the first one is due: folded and no longer listed, or listed too long.
    const due = Math.min(
      ...[...leaving].map(
        ([id, { since }]) =>
          since + (listed.has(id) ? LEAVE_FALLBACK_MS : LEAVE_MS),
      ),
    );
    const timer = window.setTimeout(
      () => {
        setLeaving((current) => {
          const now = Date.now();
          const kept = new Map(current);
          for (const [id, { since }] of current) {
            const age = now - since;
            if (
              (!listed.has(id) && age >= LEAVE_MS) ||
              age >= LEAVE_FALLBACK_MS
            ) {
              kept.delete(id);
            }
          }
          return kept.size === current.size ? current : kept;
        });
      },
      Math.max(due - Date.now(), 0),
    );
    return () => {
      window.clearTimeout(timer);
    };
  }, [leaving, requests.items]);

  function answered(requestId: string) {
    const at = requests.items.findIndex((request) => request.id === requestId);
    const request = requests.items[at];
    if (request === undefined) return;
    setLeaving((now) =>
      new Map(now).set(requestId, { request, at, since: Date.now() }),
    );
  }

  // The rows: what waits, with what is folding put back where it stood.
  const rows: { request: AccessRequestView; folding: boolean }[] =
    requests.items.map((request) => ({
      request,
      folding: leaving.has(request.id),
    }));
  for (const [id, { request, at }] of leaving) {
    if (rows.some((row) => row.request.id === id)) continue;
    rows.splice(Math.min(at, rows.length), 0, { request, folding: true });
  }
  const waiting = rows.filter((row) => !row.folding);

  if (!canManage || rows.length === 0) return null;

  async function addAll() {
    setAddingAll(true);
    const added: string[] = [];
    // In turn, oldest first, as the list shows them: one request each, and a
    // refusal is said for the person it was about.
    for (const { request } of waiting) {
      try {
        await addFromRequest.mutateAsync({
          workspaceId,
          requestId: request.id,
        });
        answered(request.id);
        added.push(request.user.display_name);
      } catch (failure) {
        toast.error(
          `${request.user.display_name}: ${
            failure instanceof Error
              ? failure.message
              : 'This person could not be added.'
          }`,
        );
      }
    }
    setAddingAll(false);
    if (added.length === 1) toast.success(ACCESS_COPY.added(added[0] ?? ''));
    else if (added.length > 1) {
      toast.success(`${String(added.length)} people were added.`);
    }
  }

  return (
    <section
      aria-labelledby="access-requests-heading"
      className="border-board-state-waiting/40 bg-board-state-waiting/5 flex flex-col gap-2 rounded-lg border p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2
          id="access-requests-heading"
          className="text-foreground text-sm font-medium"
        >
          {askedToJoin(waiting.length)}
        </h2>
        {waiting.length > 1 ? (
          <Button
            size="sm"
            variant="outline"
            disabled={addingAll}
            onClick={() => {
              void addAll();
            }}
          >
            {addingAll ? 'Adding…' : 'Add all'}
          </Button>
        ) : null}
      </div>
      <ul aria-label={ACCESS_COPY.requestsHeading} className="flex flex-col">
        {rows.map(({ request, folding }, index) => (
          <li
            key={request.id}
            aria-label={request.user.display_name}
            aria-hidden={folding || undefined}
            className={cn(
              // A grid row that can close to nothing: answered, it folds from
              // its height to none and fades, at the scale's base (US6).
              // Reduced motion closes it at once.
              'grid grid-rows-[1fr] transition-[grid-template-rows,opacity] duration-[var(--motion-duration-base)] ease-[var(--motion-ease-exit)] motion-reduce:transition-none',
              folding && 'grid-rows-[0fr] opacity-0',
            )}
          >
            <div
              className={cn(
                'min-h-0 overflow-hidden',
                index > 0 && 'border-border border-t',
              )}
            >
              <div className="flex w-full flex-wrap items-center gap-3 py-2.5">
                <UserAvatar
                  id={request.user.id}
                  displayName={request.user.display_name}
                  email={request.user.email}
                  className="size-8"
                />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="text-foreground truncate text-sm font-medium">
                    {request.user.display_name}
                  </span>
                  <span className="text-muted-foreground truncate text-xs">
                    {request.user.email} ·{' '}
                    {ACCESS_COPY.requestedWhen(
                      formatRelativeTime(request.requested_at),
                    )}
                  </span>
                </span>
                {folding ? null : (
                  <AccessRequestActions
                    workspaceId={workspaceId}
                    requestId={request.id}
                    name={request.user.display_name}
                    size="sm"
                    withOwner
                    onAnswered={answered}
                  />
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
